// A survey's colours in the filler (`viewer/Preview`): the page and bar backgrounds, the answer
// options and the main button. The author picks one of the themes below in the form builder's
// Design settings and may change any of its colours; every text colour stays the platform's own.
//
// Stored on the survey as one text field, `Theme`, next to the banner's colours: written by
// `PUT /Survey/{id}/EditSurveyBanner` and read back on `GET /Survey/GetBannerData`, the endpoint
// the public preview already calls. The value is JSON naming the theme and only the colours the
// author changed, e.g. `{"base":"ocean","colors":{"radio":"#1F5F8B"}}`.
//
// The survey header (banner) is not part of this - it keeps its own background and text colour.
// Survey-specific campaign themes (`../themes`) replace the whole page and win over these colours.

export const COLOR_FIELDS = Object.freeze([
   {
      group: "Backgrounds",
      items: [
         ["page", "Page"],
         ["lang", "Language bar"],
         ["sec", "Section titles"],
         ["bar", "Button bar"]
      ]
   },
   {
      group: "Answer options",
      items: [
         ["opt", "Option background"],
         ["optSel", "Selected option"],
         ["radio", "Selected radio / checkbox"]
      ]
   },
   {
      group: "Buttons",
      items: [["brand", "Main button"]]
   }
]);

export const COLOR_KEYS = COLOR_FIELDS.flatMap((group) => group.items.map(([key]) => key));

export const DEFAULT_THEME_ID = "kapsurvey";

// KAPsurvey is the look every survey already has. The others follow Pink October's recipe:
// near-white tints of one hue for the backgrounds, a slightly deeper tint for the selected option,
// and one deep colour (white text reads on it) for the radio and the button.
export const SURVEY_COLOR_THEMES = Object.freeze([
   {
      id: DEFAULT_THEME_ID,
      name: "KAPsurvey",
      colors: {
         page: "#EEF2FF",
         lang: "#D9E3FF",
         sec: "#EEF2FF",
         bar: "#FEFEFE",
         opt: "#FFFFFF",
         optSel: "#FFFFFF",
         radio: "#5770CB",
         brand: "#5770CB"
      }
   },
   {
      id: "pink-october",
      name: "Pink October",
      colors: {
         page: "#FAF8F9",
         lang: "#FAF3F5",
         sec: "#FAF0F3",
         bar: "#FAF3F5",
         opt: "#FCF8F9",
         optSel: "#FDF0F3",
         radio: "#B2455E",
         brand: "#B2455E"
      }
   },
   {
      id: "ocean",
      name: "Ocean",
      colors: {
         page: "#F7F9FB",
         lang: "#F1F6FA",
         sec: "#EAF2F8",
         bar: "#F1F6FA",
         opt: "#F8FAFC",
         optSel: "#E8F1F8",
         radio: "#1F5F8B",
         brand: "#1F5F8B"
      }
   },
   {
      id: "emerald",
      name: "Emerald",
      colors: {
         page: "#F7FAF8",
         lang: "#F1F8F4",
         sec: "#E9F4EE",
         bar: "#F1F8F4",
         opt: "#F8FBF9",
         optSel: "#E6F3EC",
         radio: "#1F7A5A",
         brand: "#1F7A5A"
      }
   },
   {
      id: "lavender",
      name: "Lavender",
      colors: {
         page: "#F9F8FC",
         lang: "#F5F2FA",
         sec: "#EFEAF7",
         bar: "#F5F2FA",
         opt: "#FAF9FD",
         optSel: "#EEE8F7",
         radio: "#6B4FA0",
         brand: "#6B4FA0"
      }
   },
   {
      id: "terracotta",
      name: "Terracotta",
      colors: {
         page: "#FBF9F7",
         lang: "#FAF3EE",
         sec: "#F7ECE4",
         bar: "#FAF3EE",
         opt: "#FCF9F7",
         optSel: "#F8EBE2",
         radio: "#B4532A",
         brand: "#B4532A"
      }
   },
   {
      id: "teal",
      name: "Teal",
      colors: {
         page: "#F6FAFA",
         lang: "#EFF7F7",
         sec: "#E5F2F2",
         bar: "#EFF7F7",
         opt: "#F8FBFB",
         optSel: "#E2F1F0",
         radio: "#0E7C7B",
         brand: "#0E7C7B"
      }
   },
   {
      id: "olive",
      name: "Olive",
      colors: {
         page: "#F9FAF6",
         lang: "#F5F7EF",
         sec: "#EEF1E4",
         bar: "#F5F7EF",
         opt: "#FAFBF7",
         optSel: "#ECF0E0",
         radio: "#5B6B2F",
         brand: "#5B6B2F"
      }
   },
   {
      id: "honey",
      name: "Honey",
      colors: {
         page: "#FCFAF5",
         lang: "#FBF6E9",
         sec: "#F8F0DB",
         bar: "#FBF6E9",
         opt: "#FDFBF6",
         optSel: "#F7EED3",
         radio: "#946200",
         brand: "#946200"
      }
   },
   {
      id: "graphite",
      name: "Graphite",
      colors: {
         page: "#F8F9FA",
         lang: "#F3F4F6",
         sec: "#ECEEF1",
         bar: "#F3F4F6",
         opt: "#F9FAFB",
         optSel: "#E9ECF0",
         radio: "#3E4A5C",
         brand: "#3E4A5C"
      }
   }
]);

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** `#abc`, `abc` or `#AABBCC` as `#AABBCC`; anything else as null. */
export const normalizeHex = (value) => {
   const match = HEX.exec(String(value ?? "").trim());
   if (!match) return null;

   const digits =
      match[1].length === 3
         ? match[1]
              .split("")
              .map((char) => char + char)
              .join("")
         : match[1];

   return `#${digits.toUpperCase()}`;
};

export const getColorTheme = (themeId) =>
   SURVEY_COLOR_THEMES.find((theme) => theme.id === themeId) ??
   SURVEY_COLOR_THEMES.find((theme) => theme.id === DEFAULT_THEME_ID);

/**
 * The stored `Theme` text as `{ base, edits }`, keeping only known colours that differ from the
 * theme's own. Empty, unreadable or foreign values read as the plain KAPsurvey look, so a bad
 * value can never stop a respondent from seeing the survey.
 */
export const parseSurveyColors = (raw) => {
   let value = raw;

   if (typeof raw === "string") {
      try {
         value = raw.trim() ? JSON.parse(raw) : null;
      } catch (error) {
         value = null;
      }
   }

   const theme = getColorTheme(value?.base);
   const edits = {};

   COLOR_KEYS.forEach((key) => {
      const color = normalizeHex(value?.colors?.[key]);
      if (color && color !== theme.colors[key]) edits[key] = color;
   });

   return { base: theme.id, edits };
};

/** `{ base, edits }` as the text stored in the survey's `Theme` field. */
export const serializeSurveyColors = ({ base, edits }) => {
   const { id, colors } = getColorTheme(base);
   const changed = {};

   COLOR_KEYS.forEach((key) => {
      const color = normalizeHex(edits?.[key]);
      if (color && color !== colors[key]) changed[key] = color;
   });

   return JSON.stringify(
      Object.keys(changed).length ? { base: id, colors: changed } : { base: id }
   );
};

/** The full set of colours for `{ base, edits }`. */
export const resolveColors = ({ base, edits }) => ({ ...getColorTheme(base).colors, ...edits });

/**
 * The colours the filler should draw a survey in, or null when it should keep the plain
 * KAPsurvey look - no value, an unreadable one, or KAPsurvey with nothing changed.
 */
export const resolveSurveyColors = (raw) => {
   const parsed = parseSurveyColors(raw);

   if (parsed.base === DEFAULT_THEME_ID && !Object.keys(parsed.edits).length) {
      return null;
   }

   return resolveColors(parsed);
};

const toRgb = (hex) => [1, 3, 5].map((index) => parseInt(hex.slice(index, index + 2), 16));

const luminance = (hex) => {
   const channel = (value) => {
      const scaled = value / 255;
      return scaled <= 0.03928 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4;
   };
   const [r, g, b] = toRgb(hex).map(channel);

   return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** White when it reads better on `hex` than `dark` does, otherwise `dark`. */
export const inkOn = (hex, dark = "#252938") => {
   const light = luminance(hex);
   return 1.05 / (light + 0.05) >= (light + 0.05) / 0.05 ? "#FFFFFF" : dark;
};

/** `weight` of `color` over `base`, as an opaque hex. */
export const mix = (color, base, weight) => {
   const over = toRgb(base);
   return `#${toRgb(color)
      .map((channel, index) =>
         Math.round(channel * weight + over[index] * (1 - weight))
            .toString(16)
            .padStart(2, "0")
      )
      .join("")
      .toUpperCase()}`;
};

/**
 * Inline style exposing the colours as the `--sc-*` variables `surveyColors.scss` reads.
 *
 * Borders, section titles and the secondary buttons follow the main button; the selected
 * option's border follows the radio. Text on a background flips to white when the background is
 * dark. The tints are worked out here rather than with `color-mix` because the Sass this project
 * builds with predates it.
 */
export const surveyColorVars = (colors) => ({
   "--sc-page": colors.page,
   "--sc-lang": colors.lang,
   "--sc-sec": colors.sec,
   "--sc-bar": colors.bar,
   "--sc-bar-end": mix(colors.bar, "#000000", 0.92),
   "--sc-opt": colors.opt,
   "--sc-opt-sel": colors.optSel,
   "--sc-radio": colors.radio,
   "--sc-brand": colors.brand,
   "--sc-brand-soft": mix(colors.brand, "#FFFFFF", 0.08),
   "--sc-brand-hover": mix(colors.brand, "#000000", 0.82),
   "--sc-shadow": `${colors.brand}26`,
   "--sc-line": mix(colors.brand, "#FFFFFF", 0.5),
   "--sc-sel-line": mix(colors.radio, "#FFFFFF", 0.55),
   "--sc-lang-ink": inkOn(colors.lang, "#3A3737"),
   "--sc-sec-ink": inkOn(colors.sec, colors.brand),
   "--sc-opt-ink": inkOn(colors.opt, "#7C7C80"),
   "--sc-opt-sel-ink": inkOn(colors.optSel, "#7C7C80"),
   "--sc-radio-ink": inkOn(colors.radio, "#17181C"),
   "--sc-brand-ink": inkOn(colors.brand, "#17181C")
});
